import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { UX2, constants, utils as CoreUtils } from '@wsb/guac-widget-core';
import dataAids from '../../constants/dataAids';
import platforms from '../../constants/platforms';
import { CHANNEL, URL } from '../../constants/widget/keys';
import { getFeaturePolicies } from '../utils/iframe';
import { usesChannel, hasSeparateChat } from '../utils/platform';
import {
  STREAM_WIDTH,
  CHAT_WIDTH,
  DEFAULT_HEIGHT,
  getStreamDimensions,
  getChatDimensions
} from '../utils/sizes';
import { buildStreamUrl, buildChatUrl } from '../utils/url';
import ChatPlaceholder from './ChatPlaceholder';

const { LIVESTREAM_STREAM_RENDERED, LIVESTREAM_CHAT_RENDERED } = dataAids;
const { TWITCH, YOUTUBE } = platforms;
const { EDIT } = constants.renderModes;
const { withSizeDetection } = CoreUtils;

function Livestream({
  staticContent,
  renderMode,
  viewDevice,
  size,
  platform,
  channel,
  url,
  enableChat
}) {
  const showChat = hasSeparateChat(platform) && enableChat;
  const showStream = !!(usesChannel(platform) ? channel : url);
  const responsiveProps = { viewDevice, size };

  const containerRef = useRef(null);
  const [streamDimensions, setStreamDimensions] = useState({
    width: STREAM_WIDTH,
    height: DEFAULT_HEIGHT
  });
  const [chatDimensions, setChatDimensions] = useState({
    width: CHAT_WIDTH,
    height: DEFAULT_HEIGHT
  });

  useEffect(() => {
    if (containerRef.current) {
      setStreamDimensions(
        getStreamDimensions({
          ...responsiveProps,
          renderMode,
          showChat,
          containerWidth: containerRef.current.clientWidth
        })
      );
      setChatDimensions(getChatDimensions({ ...responsiveProps, renderMode }));
    }
  }, [containerRef, size, platform, enableChat]);

  const isSSR = typeof window === 'undefined';
  const parentDomain = !isSSR && window.location.hostname;
  const urlOpts = { platform, channel, url, parentDomain };
  const routeIdentifier = usesChannel(platform) ? CHANNEL : URL;

  const styles = {
    container: {
      textAlign: 'center',
      display: 'flex',
      flexDirection: 'column',
      ['@md']: {
        flexDirection: 'row',
        justifyContent: 'center'
      }
    },
    streamContainer: {
      display: 'flex',
      ...streamDimensions,
      iframe: {
        position: 'initial'
      }
    },
    chatContainer: {
      ...chatDimensions,
      iframe: {
        position: 'initial'
      }
    }
  };

  const commonProps = {
    frameBorder: 0,
    scrolling: 'no',
    ...(renderMode === EDIT && {
      style: {
        pointerEvents: 'none'
      }
    })
  };

  const stream = (
    <UX2.Element.Block
      data-route={ routeIdentifier }
      data-field-id={ routeIdentifier }
      data-field-path={ routeIdentifier }
      data-aid={ LIVESTREAM_STREAM_RENDERED }
      style={ styles.streamContainer }
    >
      <UX2.Element.Embed
        src={ !isSSR && buildStreamUrl({ ...urlOpts, ...responsiveProps, renderMode }) }
        title={ staticContent.accessibilityTitleStream }
        allow={ getFeaturePolicies(platform) }
        { ...commonProps }
        { ...streamDimensions }
      />
    </UX2.Element.Block>
  );

  const chat =
    renderMode === EDIT ? (
      <ChatPlaceholder { ...chatDimensions } staticContent={ staticContent } />
    ) : (
      <UX2.Element.Block data-aid={ LIVESTREAM_CHAT_RENDERED } style={ styles.chatContainer }>
        <UX2.Element.Embed
          src={ !isSSR && buildChatUrl(urlOpts) }
          title={ staticContent.accessibilityTitleChat }
          { ...commonProps }
          { ...chatDimensions }
        />
      </UX2.Element.Block>
    );

  return (
    <div ref={ containerRef }>
      <UX2.Element.Block style={ styles.container }>
        {showStream && stream}
        {showStream && showChat && chat}
      </UX2.Element.Block>
    </div>
  );
}

Livestream.propTypes = {
  staticContent: PropTypes.object,
  renderMode: PropTypes.oneOf(Object.values(constants.renderModes)),
  viewDevice: PropTypes.oneOf(Object.values(constants.renderDevices)),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  platform: PropTypes.oneOf([TWITCH, YOUTUBE]),
  channel: PropTypes.string,
  url: PropTypes.string,
  enableChat: PropTypes.bool
};

export default withSizeDetection(Livestream);
